import { object, RefinementCtx, string, undefined, union, z, ZodIssueCode } from 'zod';

const editProfileSchema = object({
    firstname: string().nonempty({ message: 'Please enter a first name.' }),
    lastname: string().nonempty({ message: 'Please enter a last name.' }),
    email: string()
        .nonempty({ message: 'Please provide an email.' })
        .email({ message: 'Please provide a valid email address' }),
    roleId: string().nonempty({ message: 'Please select a role.' }),
    password: union([string(), string().regex(/(?=.*\d)(?=.*[A-Z])(?=.*[@$!%*#?&^-]).{8,}/)]),
    repeatPassword: union([string(), string().regex(/(?=.*\d)(?=.*[A-Z])(?=.*[@$!%*#?&^-]).{8,}/)]),
}).superRefine((val, ctx: RefinementCtx) => {
    if ((val.password && !val.repeatPassword) || (!val.password && val.repeatPassword)) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            message: 'Password and Repeat Password both are required.',
        });
    }
    if (val.password !== val.repeatPassword) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            path: ['repeatPassword'],
            message: 'Password and Repeat password does not match.',
        });
    }
});

export const dateSchema = z.preprocess((arg) => {
    if (typeof arg === 'string' || arg instanceof Date) {
        return new Date(arg);
    }
    return arg;
}, z.date());

export type DateSchema = z.infer<typeof dateSchema>;

export const editMemberSchema = object({
    firstname: string().nonempty({ message: 'Please enter a first name.' }),
    lastname: string().nonempty({ message: 'Please enter a last name.' }),
    email: string()
        .nonempty({ message: 'Please provide an email.' })
        .email({ message: 'Please provide a valid email address.' }),
    employerId: string().nonempty({ message: 'Please select a partner.' }),
    phone: string()
        .nonempty({ message: 'Please provide a phone number.' })
        .regex(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/, 'Please provide a valid new phone number.'),
    dateOfBirth: string().or(dateSchema).optional().nullable(),
    subscriberId: string().optional().nullable(),
    address1: string().optional(),
    address2: string().optional().nullable(),
    city: string().optional(),
    state: string().optional(),
    zip: string().optional(),
}).superRefine((val, ctx: RefinementCtx) => {
    if (val.address1 || val.address2 || val.city || val.state || val.zip) {
        if (!val.address1) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['address1'],
                message: 'Please enter a address.',
            });
        }
        if (!val.city) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['city'],
                message: 'Please enter a city.',
            });
        }
        if (!val.state) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['state'],
                message: 'Please select a state.',
            });
        }

        //  verifying a zip code with multiple validations
        if (!val.zip) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['zip'],
                message: 'Please enter a zip code.',
            });
        } else if (!/^\d+$/.test(val.zip)) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['zip'],
                message: 'Only numbers are allowed',
            });
        } else if (val.zip.length !== 5) {
            ctx.addIssue({
                code: ZodIssueCode.custom,
                path: ['zip'],
                message: 'Please enter only 5 digit number',
            });
        }
    }
});

export const addMemberAddressSchema = object({
    address1: string().nonempty({ message: 'Please enter a address.' }),
    address2: string().optional(),
    city: string().nonempty({ message: 'Please enter a city.' }),
    state: string().nonempty({ message: 'Please select a state.' }),
    zip: string()
        .nonempty({ message: 'Please provide a zip code.' })
        .regex(/^\d+$/, 'Only numbers are allowed')
        .length(5, 'Please enter only 5 digit number'),
});

export const updateMemberAddressSchema = object({
    id: string().nonempty({ message: 'Address Id should not be empty' }),
    address1: string().nonempty({ message: 'Please enter a address.' }),
    address2: string().optional().nullable(),
    city: string().nonempty({ message: 'Please enter a city.' }),
    state: string().nonempty({ message: 'Please select a state.' }),
    zip: string()
        .nonempty({ message: 'Please provide a zip code.' })
        .regex(/^\d+$/, 'Only numbers are allowed')
        .length(5, 'Please enter only 5 digit number'),
});

export const createMemberSchema = object({
    selectedPartnerId: union([string(), undefined()]).refine((val) => val, { message: 'Partner should not be empty.' }),
    firstname: string().nonempty({ message: 'Please enter a first name.' }),
    lastname: string().nonempty({ message: 'Please enter a last name.' }),
    email: string()
        .nonempty({ message: 'Please provide an email.' })
        .email({ message: 'Please provide a valid email address' }),
    needs_welcome_kit: string().nonempty({ message: 'Please select need of welcome kit.' }),
    phone: string()
        .nonempty({ message: 'Please provide a phone number.' })
        .regex(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/, 'Please provide a valid new phone number.'),
    dateOfBirth: dateSchema,
    address1: string().nonempty({ message: 'Please enter a address.' }),
    address2: string().optional(),
    city: string().nonempty({ message: 'Please enter a city.' }),
    state: string().nonempty({ message: 'Please select a state.' }),
    zip: string()
        .nonempty({ message: 'Please provide a zip code.' })
        .regex(/^\d+$/, 'Only numbers are allowed')
        .length(5, 'Please enter only 5 digit number'),
});

export default editProfileSchema;
